import {getUrlParams} from 'shared/utils/url-utils';
import type {ILocationService, ILogService, IRootScopeService} from 'angular';
import type {Observable} from 'rxjs';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {filter, pairwise} from 'rxjs/operators';
import type {StateManager} from 'typings/portaro.fe.types';

type StateChangeHandler = (params: Record<string, any>) => Promise<any>;

export class UrlStateManagerService implements StateManager {
    public static serviceName = 'urlStateManagerService';

    private readonly stateChangeHandlers: StateChangeHandler[] = [];
    private readonly pendingPromises: Promise<void>[] = [];
    private locationChangeEventAlreadyFiredForThisPath = false;
    private currentPathId: string = null;

    private readonly loadingState$: BehaviorSubject<boolean>;
    private readonly loadingFinished$: Observable<any> // loading finished events

    /*@ngInject*/
    constructor(private $location: ILocationService,
                private $rootScope: IRootScopeService,
                private $log: ILogService) {
        this.registerLocationChangeListeners();
        this.loadingState$ = new BehaviorSubject<boolean>(this.isLoading());
        this.loadingFinished$ = this.loadingState$.pipe(
            pairwise(), // pair all state changes (T,F,T,F -> [T,F], [F,T], [T,F] )
            filter(([firstIsLoading, thenIsLoading]) => firstIsLoading && !thenIsLoading), // get falling isLoading changes (T -> F)
        )
    }

    public register(stateChangeHandler: StateChangeHandler): () => void {
        this.$log.info('registered on url params change handler');
        this.stateChangeHandlers.push(stateChangeHandler);
        if (this.locationChangeEventAlreadyFiredForThisPath) { // if this stateChangeHandler was registered just after location change event, we must manually call this stateChangeHandler
            this.fireStateChangeHandler(stateChangeHandler);
        }
        return () => this.deregister(stateChangeHandler);
    }

    public requestChangeState<T extends Record<string, any>>(newParams: T): Promise<void> {
        // nastavime jen zmeny oproti "?" parametrum, ktere zmenit nejdou
        const currentUrlParams = getUrlParams();
        const paramsToAddToHash: Record<string, any> = {};
        for (const propName in newParams) {
            if (Object.hasOwn(newParams, propName)) { // sanity check
                if (!Object.hasOwn(currentUrlParams, propName) || newParams[propName] !== currentUrlParams[propName]) {
                    paramsToAddToHash[propName] = newParams[propName];
                }
            }
        }
        this.$location.search(paramsToAddToHash);
        this.$rootScope.$applyAsync(); // to notify angular if change request was called from outside angular context (e.g. from svelte)

        return firstValueFrom(this.loadingFinished$);
    }

    // params can be whatever dictionary(Record<string, any>) object, but name of the changed parameter has to exist on that params object and it has to be string (not symbol or number)
    public requestChangeStateParameter<T extends Record<string, any>, K extends keyof T>(parameterName: Exclude<K, number | symbol>, newValue: T[Exclude<K, number | symbol>]): Promise<void> {
        // nastavime jen zmeny oproti "?" parametrum, ktere zmenit nejdou
        const urlParams = getUrlParams();
        if (!Object.hasOwn(urlParams, parameterName) || urlParams[parameterName] !== newValue) {
            this.$location.search(parameterName, newValue);
            this.$rootScope.$applyAsync(); // to notify angular if change request was called from outside angular context (e.g. from svelte)
        }

        return firstValueFrom(this.loadingFinished$);
    }

    public isLoading(): boolean {
        return this.pendingPromises.length !== 0;
    }

    public isLoading$(): Observable<boolean> {
        return this.loadingState$.asObservable();
    }

    private deregister(stateChangeHandler: StateChangeHandler): void {
        this.$log.info('deregistering stateChangeHandler');
        this.stateChangeHandlers.splice(this.stateChangeHandlers.indexOf(stateChangeHandler), 1);
    }

    private currentParams(): Record<string, any> {
        return {...getUrlParams(), ...this.$location.search()};
    }

    private buildCurrentPathId(): string {
        return `${location.pathname}#${this.$location.path()}`; // current path is combination of standard path and hash path
    }

    private fireAllPromises(): Promise<any[]> {
        const promises = this.stateChangeHandlers.map((stateChangeHandler) => this.fireStateChangeHandler(stateChangeHandler));
        return Promise.all(promises);
    }

    private fireStateChangeHandler(stateChangeHandler: StateChangeHandler): Promise<any> {
        let newParams = this.currentParams();
        if (Object.keys(newParams).length === 0) {
            newParams = {};
        }

        const promise = stateChangeHandler(newParams);

        const removePromise = () => {
            this.pendingPromises.splice(this.pendingPromises.indexOf(promise), 1);
            this.loadingState$.next(this.isLoading());
        };
        promise.finally(() => removePromise());

        this.pendingPromises.push(promise);
        this.loadingState$.next(this.isLoading());
        return promise;
    }

    private registerLocationChangeListeners(): void {
        this.registerLocationChangeStartEventListener();
        this.registerLocationChangeSuccessEventListener();
    }

    private registerLocationChangeStartEventListener(): void {
        // location change start listener for setting stateChangeHandlers-already-called flag
        this.$rootScope.$on('$locationChangeStart',  () => {
            const lastPath = this.currentPathId;
            this.currentPathId = this.buildCurrentPathId();
            if (this.currentPathId !== lastPath) {
                this.$log.info('location path change started');
                this.locationChangeEventAlreadyFiredForThisPath = false;
            }
        });
    }

    private registerLocationChangeSuccessEventListener(): void {
        // on location change listener
        this.$rootScope.$on('$locationChangeSuccess', () => {
            this.$log.info(`location changed -> firing all (${this.stateChangeHandlers.length}) stateChangeHandlers`);
            this.fireAllPromises();
            this.locationChangeEventAlreadyFiredForThisPath = true;
        });
    }
}